import React, { useState, useEffect } from 'react'
import { useSidebar } from '../../hooks/useSidebar'
import styled from 'styled-components'
import { Sidebar } from '../organisms/sidebar/Sidebar'
import { Outlet } from 'react-router-dom'
import { MobileHeader } from '../molecules/MobileHeader'
import { MobileOverlay } from '../atoms/MobileOverlay'

export const SidebarTemplate: React.FC = () => {
  const {
    username,
    sidebarMenusToShow,
    selectedMenuPath,
    onSidebarMenuClick,
    handleLogout
  } = useSidebar()

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Close mobile sidebar when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent background scrolling when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      // Store original body and document element styles
      const body = document.body;
      const documentElement = document.documentElement;
      
      const originalBodyOverflow = body.style.overflow;
      const originalBodyPosition = body.style.position;
      const originalBodyTop = body.style.top;
      const originalBodyWidth = body.style.width;
      const originalDocumentOverflow = documentElement.style.overflow;
      
      // Get current scroll position
      const scrollY = window.scrollY;
      const scrollBarWidth = window.innerWidth - documentElement.clientWidth;
      
      // Apply comprehensive scroll lock
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      body.style.paddingRight = `${scrollBarWidth}px`;
      documentElement.style.overflow = 'hidden';
      
      // Prevent touch scrolling on iOS - but only outside sidebar content
      const preventTouchMove = (e: TouchEvent) => {
        // Allow scrolling within sidebar content
        const target = e.target as Element;
        const sidebarContent = document.querySelector('[data-sidebar-content]');
        if (sidebarContent && sidebarContent.contains(target)) {
          return; // Allow scrolling within sidebar
        }
        e.preventDefault();
      };
      
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      // Cleanup function to restore original state
      return () => {
        body.style.overflow = originalBodyOverflow;
        body.style.position = originalBodyPosition;
        body.style.top = originalBodyTop;
        body.style.width = originalBodyWidth;
        body.style.paddingRight = '';
        documentElement.style.overflow = originalDocumentOverflow;
        
        document.removeEventListener('touchmove', preventTouchMove);
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileSidebarOpen]);

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  return (
    <StyledSideBarTemplateContainer>
      <MobileHeader 
        isSidebarOpen={isMobileSidebarOpen}
        onToggleSidebar={toggleMobileSidebar}
      />
      <MobileOverlay 
        isOpen={isMobileSidebarOpen}
        onClick={closeMobileSidebar}
      />
      <StyledSideBarContainter>
        <Sidebar 
          username={username}
          sidebarMenuToShow={sidebarMenusToShow}
          selectedMenuPath={selectedMenuPath}
          onSidebarMenuClick={onSidebarMenuClick}
          handleLogout={handleLogout}
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={closeMobileSidebar}
        />
        <StyledOutletContainer>
          <Outlet />
        </StyledOutletContainer>
      </StyledSideBarContainter>
    </StyledSideBarTemplateContainer>
  )
}

const StyledSideBarTemplateContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  overflow: hidden;

  @media (min-width: 769px) {
    flex-direction: row;
  }
`;

const StyledSideBarContainter = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 769px) {
    flex-direction: row;
  }
`;

const StyledOutletContainer = styled.div`
  flex: 1;
  height: calc(100vh - 70px);
  display: flex;
  overflow: hidden;

  @media (min-width: 769px) {
    height: 100vh;
  }
`;